import React from 'react';
import { X, Calendar, DollarSign, User, MapPin, Target, Link as LinkIcon, FileText, Zap, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const AdsRequestDetailModal = ({ request, onClose }) => {
  const getPriorityConfig = (prioridad) => {
    switch (prioridad) {
      case 'urgente':
        return { bg: 'from-red-500 to-red-600', icon: '🔥', label: 'URGENTE' };
      case 'alta':
        return { bg: 'from-orange-500 to-orange-600', icon: '⚡', label: 'ALTA' };
      case 'normal':
        return { bg: 'from-blue-500 to-blue-600', icon: '📌', label: 'NORMAL' };
      case 'baja':
        return { bg: 'from-gray-500 to-gray-600', icon: '📋', label: 'BAJA' };
      default:
        return { bg: 'from-gray-500 to-gray-600', icon: '📋', label: 'NORMAL' };
    }
  };

  const getEstadoConfig = (estado) => {
    switch (estado) {
      case 'pendiente':
        return { bg: 'bg-gradient-to-r from-yellow-400 to-yellow-500', text: 'text-yellow-900', icon: '⏳', label: 'Pendiente' };
      case 'en_proceso':
        return { bg: 'bg-gradient-to-r from-blue-400 to-blue-500', text: 'text-blue-900', icon: '🔄', label: 'En Proceso' };
      case 'aprobado':
        return { bg: 'bg-gradient-to-r from-green-400 to-green-500', text: 'text-green-900', icon: '✅', label: 'Aprobado' };
      case 'rechazado':
        return { bg: 'bg-gradient-to-r from-red-400 to-red-500', text: 'text-red-900', icon: '❌', label: 'Rechazado' };
      case 'completado':
        return { bg: 'bg-gradient-to-r from-purple-400 to-purple-500', text: 'text-purple-900', icon: '🎉', label: 'Completado' };
      default:
        return { bg: 'bg-gradient-to-r from-gray-400 to-gray-500', text: 'text-gray-900', icon: '📄', label: estado };
    }
  };

  const getTipoConfig = (tipo) => {
    return tipo === 'nueva' 
      ? { bg: 'bg-gradient-to-r from-emerald-400 to-emerald-500', text: 'text-emerald-900', icon: '🆕', label: 'Campaña Nueva' }
      : { bg: 'bg-gradient-to-r from-cyan-400 to-cyan-500', text: 'text-cyan-900', icon: '🔄', label: 'Campaña Actual' };
  };

  const getPlatformConfig = (plataforma) => {
    return plataforma === 'meta'
      ? { bg: 'bg-gradient-to-r from-purple-500 to-indigo-600', icon: '📘', label: 'Meta Ads' }
      : { bg: 'bg-gradient-to-r from-blue-500 to-indigo-600', icon: '🔍', label: 'Google Ads' };
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    try {
      return format(new Date(fecha), "d 'de' MMMM, yyyy 'a las' HH:mm", { locale: es });
    } catch (error) {
      return fecha;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const priorityConfig = getPriorityConfig(request.prioridad);
  const estadoConfig = getEstadoConfig(request.estado);
  const tipoConfig = getTipoConfig(request.tipo_campana);
  const platformConfig = getPlatformConfig(request.plataforma);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden">
        {/* Header con gradiente de prioridad */}
        <div className={`relative bg-gradient-to-r ${priorityConfig.bg} p-4 text-white`}>
          <div className="absolute top-0 left-0 w-full h-full bg-black opacity-10"></div>
          <div className="relative flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{priorityConfig.icon}</span>
                <div>
                  <h2 className="text-xl font-bold">{request.nombre_cliente}</h2>
                  <p className="text-white/90 text-xs font-medium mt-0.5">Solicitud de Anuncios</p>
                </div>
              </div>
              
              {/* Badges en el header */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${platformConfig.bg} text-white shadow-md`}>
                  <span className="mr-1">{platformConfig.icon}</span>
                  {platformConfig.label}
                </span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${tipoConfig.bg} ${tipoConfig.text} shadow-md`}>
                  <span className="mr-1">{tipoConfig.icon}</span>
                  {tipoConfig.label}
                </span>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-white/20 text-white border border-white/40 shadow-md">
                  <AlertCircle size={12} className="mr-1" />
                  {priorityConfig.label}
                </span>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="relative bg-white/20 hover:bg-white/30 p-2 rounded-full transition-all duration-200"
            >
              <X size={20} className="text-white" />
            </button>
          </div>
        </div>

        {/* Contenido scrolleable */}
        <div className="overflow-y-auto max-h-[calc(85vh-180px)] p-4">
          {/* Estado destacado */}
          <div className="mb-4">
            <span className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold ${estadoConfig.bg} ${estadoConfig.text} shadow-lg`}>
              <span className="mr-2 text-lg">{estadoConfig.icon}</span>
              Estado: {estadoConfig.label}
            </span>
          </div>

          {/* Grid de información principal */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Nombre de Campaña */}
            {request.nombre_campana && (
              <div className="col-span-full bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200 shadow-sm">
                <div className="flex items-start">
                  <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2 rounded-lg mr-3">
                    <Target size={18} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-blue-600 uppercase mb-1">Nombre de la Campaña</p>
                    <p className="text-base font-bold text-gray-900">{request.nombre_campana}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Presupuesto Diario */}
            {request.presupuesto_diario && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200 shadow-sm">
                <div className="flex items-center mb-2">
                  <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-2 rounded-lg mr-2">
                    <DollarSign size={18} className="text-white" />
                  </div>
                  <p className="text-xs font-semibold text-green-700 uppercase">Presupuesto Diario</p>
                </div>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(request.presupuesto_diario)}</p>
              </div>
            )}

            {/* Asignado a */}
            {request.asignado_a && (
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 rounded-xl border border-purple-200 shadow-sm">
                <div className="flex items-center mb-2">
                  <div className="bg-gradient-to-br from-purple-500 to-indigo-600 p-2 rounded-lg mr-2">
                    <User size={18} className="text-white" />
                  </div>
                  <p className="text-xs font-semibold text-purple-700 uppercase">Asignado a</p>
                </div>
                <p className="text-base font-bold text-gray-900">{request.asignado_a}</p>
              </div>
            )}
          </div>

          {/* Detalles adicionales */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
              <div className="w-1.5 h-6 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full mr-2"></div>
              Detalles de la Solicitud
            </h3>

            {/* Ubicación Objetivo */}
            {request.ubicacion_objetivo && (
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-start">
                  <MapPin size={16} className="mr-2 mt-0.5 text-red-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Ubicación Objetivo</p>
                    <p className="text-sm font-medium text-gray-900">{request.ubicacion_objetivo}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Radio en KM */}
            {request.radio_km && (
              <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-1.5 rounded mr-2">
                    <span className="text-lg">📍</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Radio de Cobertura</p>
                    <p className="text-sm font-medium text-gray-900">{request.radio_km} km</p>
                  </div>
                </div>
              </div>
            )}

            {/* Palabras Clave */}
            {request.palabras_clave && (
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-3 rounded-lg border border-yellow-200 shadow-sm">
                <div className="flex items-start">
                  <Zap size={16} className="mr-2 mt-0.5 text-yellow-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-yellow-700 uppercase mb-1">Palabras Clave</p>
                    <p className="text-sm font-medium text-gray-900">{request.palabras_clave}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Landing Page */}
            {request.landing_page && (
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-3 rounded-lg border border-blue-200 shadow-sm">
                <div className="flex items-start">
                  <LinkIcon size={16} className="mr-2 mt-0.5 text-blue-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-blue-700 uppercase mb-1">Landing Page</p>
                    <a 
                      href={request.landing_page} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline break-all"
                    >
                      {request.landing_page}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Link Cuenta Ads */}
            {request.link_cuenta_ads && (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-3 rounded-lg border border-indigo-200 shadow-sm">
                <div className="flex items-start">
                  <LinkIcon size={16} className="mr-2 mt-0.5 text-indigo-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-indigo-700 uppercase mb-1">Cuenta de Anuncios</p>
                    <a 
                      href={request.link_cuenta_ads} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline break-all"
                    >
                      {request.link_cuenta_ads}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Observaciones */}
            {request.observaciones && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-3 rounded-lg border border-purple-200 shadow-sm">
                <div className="flex items-start">
                  <FileText size={16} className="mr-2 mt-0.5 text-purple-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-purple-700 uppercase mb-1">Observaciones</p>
                    <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">{request.observaciones}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Fecha de Creación */}
            {request.created_at && (
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center">
                  <Calendar size={16} className="mr-2 text-gray-500" />
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase">Fecha de Creación</p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">{formatFecha(request.created_at)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdsRequestDetailModal;
